import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { map, switchMap, take, tap } from 'rxjs/operators';
import { environment } from 'environments/environment';
import {
  FounderCircle,
  FounderCircleCreateRequest,
  FounderCircleListResponse,
  FounderCircleResponse,
  FounderCircleUpdateRequest,
  FounderCircleEnableDisableResponse,
} from '../models';

@Injectable({ providedIn: 'root' })
export class FounderCircleService {
  private _allFounderCircles: BehaviorSubject<FounderCircle[] | null> = new BehaviorSubject(null);

  // -----------------------------------------------------------------------------------------------------
  // @ Accessors
  // -----------------------------------------------------------------------------------------------------

  get allFounderCircles$(): Observable<FounderCircle[]> {
    return this._allFounderCircles.asObservable();
  }

  // -----------------------------------------------------------------------------------------------------
  // @ Constructor
  // -----------------------------------------------------------------------------------------------------

  constructor(private _httpClient: HttpClient) {}

  // -----------------------------------------------------------------------------------------------------
  // @ Public methods
  // -----------------------------------------------------------------------------------------------------

  /**
   * Get all founder circles
   */
  getAll(includeDisabled: boolean = false): Observable<FounderCircleListResponse> {
    return this._httpClient
      .get<FounderCircleListResponse>(environment.apiURL + 'founder-circles/all', {
        params: { includeDisabled },
      })
      .pipe(
        tap((response) => {
          this._allFounderCircles.next(response.founderCircles);
        })
      );
  }

  /**
   * Get founder circle by ID
   */
  getById(id: string): Observable<FounderCircleResponse> {
    return this._httpClient.get<FounderCircleResponse>(environment.apiURL + `founder-circles/byId/${id}`);
  }

  /**
   * Create founder circle
   */
  create(data: FounderCircleCreateRequest): Observable<FounderCircleResponse> {
    return this.allFounderCircles$.pipe(
      take(1),
      switchMap((founderCircles) =>
        this._httpClient.post<FounderCircleResponse>(environment.apiURL + 'founder-circles/add', data).pipe(
          map((response) => {
            // Add the new founder circle to the list
            if (founderCircles) {
              const updatedCircles = [response.founderCircle, ...founderCircles];
              this._allFounderCircles.next(updatedCircles);
            } else {
              this._allFounderCircles.next([response.founderCircle]);
            }
            return response;
          })
        )
      )
    );
  }

  /**
   * Update founder circle
   */
  update(id: string, data: FounderCircleUpdateRequest): Observable<FounderCircleResponse> {
    return this.allFounderCircles$.pipe(
      take(1),
      switchMap((founderCircles) =>
        this._httpClient.put<FounderCircleResponse>(environment.apiURL + `founder-circles/edit/${id}`, data).pipe(
          map((response) => {
            // Update the founder circle in the list
            if (founderCircles) {
              const index = founderCircles.findIndex((circle) => circle._id === id);
              if (index !== -1) {
                founderCircles[index] = response.founderCircle;
                this._allFounderCircles.next([...founderCircles]);
              }
            }
            return response;
          })
        )
      )
    );
  }

  /**
   * Enable/Disable founder circle
   */
  enableDisable(id: string): Observable<FounderCircleEnableDisableResponse> {
    return this.allFounderCircles$.pipe(
      take(1),
      switchMap((founderCircles) =>
        this._httpClient
          .get<FounderCircleEnableDisableResponse>(environment.apiURL + `founder-circles/enableDisable/${id}`)
          .pipe(
            map((response) => {
              // Update the founder circle status in the list
              if (founderCircles) {
                const index = founderCircles.findIndex((circle) => circle._id === id);
                if (index !== -1) {
                  founderCircles[index].isDisabled = response.isDisabled;
                  this._allFounderCircles.next([...founderCircles]);
                }
              }
              return response;
            })
          )
      )
    );
  }

  /**
   * Delete founder circle
   */
  delete(id: string): Observable<{ message: string }> {
    return this.allFounderCircles$.pipe(
      take(1),
      switchMap((founderCircles) =>
        this._httpClient.delete<{ message: string }>(environment.apiURL + `founder-circles/delete/${id}`).pipe(
          map((response) => {
            // Remove the founder circle from the list
            if (founderCircles) {
              const filteredCircles = founderCircles.filter((circle) => circle._id !== id);
              this._allFounderCircles.next(filteredCircles);
            }
            return response;
          })
        )
      )
    );
  }
}
